import React from 'react';
import pluralize from 'pluralize';
import * as styles from './FlashMessage.module.scss';
import { Banner, BannerIcon, Button, IconList, Text } from 'components/core';
import { Inline } from 'components/layout';
import { ButtonType, ButtonVariant } from 'components/core/Button';
import { BannerType } from 'components/core/Banner/Banner';
import { IconType } from 'components/core/Icon/Icon';

export type FlashMessageProps = {
  text: string;
  type: BannerType;
  ariaLabel: string;
  actionText?: string;
  actionPath?: string;
  externalAction?: boolean;
  icon?: IconType;
};

export type VerifyEmailMessageProps = FlashMessageProps & {
  onProfilePage: boolean;
};

const defaultProps = {
  type: 'warning' as const,
  icon: IconList.solid.faEnvelopeCircle,
  ariaLabel: 'Verify Email',
};

export const ProfilePageEmailVerificationFlash = (
  props: VerifyEmailMessageProps | { useDefault: true }
) => {
  return (
    <FlashMessage
      {...props}
      {...defaultProps}
      text="Please verify your email address. You can resend the verification email below."
    />
  );
};

export const VerifyEmailMessage = ({
  onProfilePage,
  ...rest
}: VerifyEmailMessageProps) => {
  if (onProfilePage) {
    return (
      <ProfilePageEmailVerificationFlash
        {...rest}
        {...defaultProps}
        onProfilePage={true}
      />
    );
  }

  return (
    <FlashMessage
      {...rest}
      {...defaultProps}
      text="Your email address on record is not verified. Please visit your profile to confirm your details."
      actionText="Update your profile"
      actionPath="/profile"
    />
  );
};

export type UpgradeAccountMessageProps = Partial<FlashMessageProps> & {
  screenLimit: string;
  reviewOwner: string;
  isReviewOwner: boolean;
};

export const UpgradeAccountMessage = ({
  screenLimit,
  isReviewOwner,
  reviewOwner,
  ...rest
}: UpgradeAccountMessageProps) => {
  const screeningRecords = `${screenLimit} ${pluralize(
    'record',
    Number(screenLimit)
  )}`;

  const howToUpgradeMessage = isReviewOwner
    ? 'upgrade to a paid plan.'
    : `ask ${reviewOwner} to upgrade to a paid plan.`;

  return (
    <FlashMessage
      {...rest}
      ariaLabel="Upgrade Account"
      text={`This review is part of a trial and is restricted to screening ${screeningRecords}. To remove this limit, ${howToUpgradeMessage}`}
      type="primary"
      actionText="Upgrade plan"
      actionPath="/plans"
      icon={IconList.solid.faCircleUp}
    />
  );
};

const iconTypes: Record<string, IconType> = {
  info: IconList.solid.faCircleInfo,
  success: IconList.solid.faCircleCheck,
  warning: IconList.solid.faCircleExclamation,
  danger: IconList.solid.faCircleXmark,
};

export const FlashMessage = ({
  ariaLabel,
  text,
  type,
  actionText,
  actionPath,
  externalAction = false,
  icon = iconTypes[type],
}: FlashMessageProps) => {
  const actionProps: Record<
    string,
    { type: ButtonType; variant: ButtonVariant }
  > = {
    primary: {
      type: 'primary',
      variant: 'outline',
    },
    warning: {
      type: 'warning',
      variant: 'outline',
    },
  };

  // Used heavily in feature tests to find the different types of banners
  const bannerTypeClassName = `alert-${type}`;

  return (
    <Banner type={type} aria-label={ariaLabel} className={bannerTypeClassName}>
      <Inline alignX="center" spacing={5}>
        <Inline className={styles.banner}>
          {icon && <BannerIcon size="lg" icon={icon} />}
          <Text>{text}</Text>
        </Inline>
        {actionText && actionPath && (
          <Button
            external={externalAction}
            to={actionPath}
            {...actionProps[type]}
          >
            {actionText}
          </Button>
        )}
      </Inline>
    </Banner>
  );
};
